import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Stack,
  Card,
  CardContent,
  useTheme,
  alpha,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Facebook as FacebookIcon,
  Chat as ChatIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Star as StarIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Info as InfoIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import AuthenticatedAvatar from '../../../components/AuthenticatedAvatar';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

interface ProfilePhoto {
  url: string;
  is_default: boolean;
  uploaded_at: string;
}

interface WorkHistory {
  company: string;
  position: string;
  start_date: string;
  end_date?: string;
  is_current: boolean;
  description?: string;
  updated_at?: string;
}

interface Alumni {
  _id: string;
  student_id: string;
  citizen_id: string;
  title: string;
  nickname?: string;
  first_name_th: string;
  last_name_th: string;
  first_name_en: string;
  last_name_en: string;
  current_first_name_th?: string;
  current_last_name_th?: string;
  current_first_name_en?: string;
  current_last_name_en?: string;
  birth_date: string;
  gender: string;
  professional_license_number?: string;
  alumni_type: string;
  batch?: string;
  ideals?: string[];
  approval_status: string;
  profile_photos?: ProfilePhoto[];
  education: {
    degree: string;
    faculty: string;
    major: string;
    graduation_year: number;
    gpa?: number;
  };
  contact: {
    email: string;
    phone: string;
    line_id?: string;
    facebook?: string;
  };
  address: {
    address_line: string;
    sub_district_code: number;
    sub_district: string;
    district_code: number;
    district: string;
    province_code: number;
    province: string;
    postal_code: string;
    country: string;
  };
  work_address?: {
    address_line: string;
    sub_district_code?: number;
    sub_district: string;
    district_code?: number;
    district: string;
    province_code?: number;
    province: string;
    postal_code: string;
    country: string;
  };
  current_occupation?: string;
  work_history?: WorkHistory[];
  user_id: string;
  created_at?: string;
  updated_at?: string;
  personal_info_updated_at?: string;
  education_updated_at?: string;
  contact_updated_at?: string;
  address_updated_at?: string;
  work_address_updated_at?: string;
  profile_photos_updated_at?: string;
}

// Helper function to format date/time in Bangkok timezone
const formatBangkokDateTime = (dateString: string) => {
  const date = new Date(dateString);
  // Add 7 hours for Bangkok timezone (UTC+7)
  const bangkokDate = new Date(date.getTime() + (7 * 60 * 60 * 1000));
  return bangkokDate.toLocaleString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`alumni-tabpanel-${index}`}
      aria-labelledby={`alumni-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function AlumniDetail() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [alumni, setAlumni] = useState<Alumni | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAlumniDetail();
  }, [id]);

  const fetchAlumniDetail = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/alumni/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlumni(response.data);
      
      // Fetch user data if user_id exists
      if (response.data.user_id) {
        try {
          const userDetailsResponse = await axios.get(
            `${API_URL}/api/v1/auth/users/${response.data.user_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setUserData(userDetailsResponse.data);
        } catch (userErr) {
          console.error('Error fetching user data:', userErr);
        }
      }
      
      setError(null);
    } catch (err: any) {
      console.error('Error fetching alumni detail:', err);
      setError(err.response?.data?.detail || 'Failed to load alumni details');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!alumni) return;
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/v1/alumni/${alumni._id}/approval`,
        { action: 'approve' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchAlumniDetail();
    } catch (err: any) {
      console.error('Error approving alumni:', err);
      setError(err.response?.data?.detail || 'Failed to approve alumni');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!alumni) return;
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/v1/alumni/${alumni._id}/approval`,
        { action: 'reject', rejection_reason: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchAlumniDetail();
    } catch (err: any) {
      console.error('Error rejecting alumni:', err);
      setError(err.response?.data?.detail || 'Failed to reject alumni');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !alumni) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('alumni.messages.errorFetch')}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/alumni')}>
          {t('common.close')}
        </Button>
      </Box>
    );
  }

  const defaultPhoto = alumni.profile_photos?.find((p) => p.is_default) || alumni.profile_photos?.[0];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', py: 2, px: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton 
              onClick={() => navigate('/alumni')} 
              sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
              }}
            >
              <ArrowBackIcon color="primary" />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {t('alumni.dialog.viewTitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('alumni.subtitle')}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={2}>
          {alumni.approval_status === 'pending' && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={handleApprove}
                disabled={actionLoading}
              >
                {t('common.approved')}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<RejectIcon />}
                onClick={handleReject}
                disabled={actionLoading}
              >
                {t('common.rejected')}
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/alumni/edit/${id}`)}
          >
            {t('common.edit')}
          </Button>
        </Stack>
        </Stack>
      </Box>

      {/* Profile Header Card */}
      <Box sx={{ px: 3 }}>
        <Card sx={{ mb: 3, overflow: 'visible', boxShadow: 3 }}>
          <Box
            sx={{
              height: 120,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
              position: 'relative',
            }}
          />
          <CardContent sx={{ position: 'relative', pt: 0, pb: 4 }}>
            <Stack 
              direction={{ xs: 'column', md: 'row' }} 
              spacing={3} 
              alignItems={{ xs: 'center', md: 'flex-start' }}
              sx={{ mt: { xs: -7, md: -7 } }}
            >
              <AuthenticatedAvatar
                src={defaultPhoto?.url}
                sx={{
                  width: 140,
                  height: 140,
                  border: `5px solid ${theme.palette.background.paper}`,
                  boxShadow: theme.shadows[12],
                  flexShrink: 0,
                }}
              />
              <Box 
                sx={{ 
                  flex: 1, 
                  mt: { xs: 2, md: 3 },
                  pt: { md: 2 },
                  width: { xs: '100%', md: 'auto' }, 
                  textAlign: { xs: 'center', md: 'left' } 
                }}
              >
                <Stack 
                  direction="row" 
                  spacing={1.5} 
                  alignItems="center" 
                  flexWrap="wrap" 
                  justifyContent={{ xs: 'center', md: 'flex-start' }}
                  sx={{ mb: 1.5 }}
                >
                  <Typography 
                    variant="h4" 
                    fontWeight="700" 
                    sx={{ 
                      mr: 1,
                      color: '#212121',
                    }}
                  >
                    {alumni.title} {alumni.first_name_th} {alumni.last_name_th}
                  </Typography>
                  <Chip
                    label={alumni.approval_status.toUpperCase()}
                    color={getStatusColor(alumni.approval_status)}
                    size="small"
                    sx={{ 
                      fontWeight: 700, 
                      height: 28,
                      fontSize: '0.75rem',
                      boxShadow: 1
                    }}
                  />
                  <Chip
                    label={alumni.alumni_type.toUpperCase()}
                    size="small"
                    sx={{ 
                      fontWeight: 700, 
                      height: 28,
                      fontSize: '0.75rem',
                      bgcolor: '#1976d2',
                      color: '#ffffff',
                      borderWidth: 0,
                      boxShadow: 1,
                      '&:hover': { bgcolor: '#1565c0' }
                    }}
                  />
                </Stack>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 500, 
                    mb: 2.5,
                    color: '#616161'
                  }}
                >
                  {alumni.first_name_en} {alumni.last_name_en}
                </Typography>
                <Stack 
                  direction="row" 
                  spacing={1.5} 
                  flexWrap="wrap" 
                  useFlexGap 
                  justifyContent={{ xs: 'center', md: 'flex-start' }}
                  sx={{ mt: 2 }}
                >
                  <Chip
                    icon={<SchoolIcon />}
                    label={`ID: ${alumni.student_id}`}
                    size="medium"
                    variant="filled"
                    sx={{ 
                      height: 32,
                      bgcolor: alpha(theme.palette.grey[500], 0.12),
                      color: '#424242',
                      fontWeight: 600,
                      '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.2) }
                    }}
                  />
                  <Chip
                    icon={<SchoolIcon />}
                    label={alumni.education.degree}
                    size="medium"
                    variant="filled"
                    sx={{ 
                      height: 32,
                      bgcolor: alpha(theme.palette.info.main, 0.12),
                      color: theme.palette.info.dark,
                      fontWeight: 600,
                      '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
                    }}
                  />
                  <Chip
                    icon={<SchoolIcon />}
                    label={alumni.education.faculty}
                    size="medium"
                    variant="filled"
                    sx={{ 
                      height: 32,
                      bgcolor: alpha(theme.palette.success.main, 0.12),
                      color: theme.palette.success.dark,
                      fontWeight: 600,
                      '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2) }
                    }}
                  />
                  <Chip
                    icon={<SchoolIcon />}
                    label={alumni.education.faculty}
                    size="medium"
                    variant="filled"
                    sx={{ 
                      height: 32,
                      bgcolor: alpha(theme.palette.warning.main, 0.12),
                      color: theme.palette.warning.dark,
                      fontWeight: 600,
                      '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.2) }
                    }}
                  />
                  <Chip
                    icon={<SchoolIcon />}
                    label={`${t('alumni.fields.graduationYear')}: ${alumni.education.graduation_year}`}
                    size="medium"
                    variant="filled"
                    sx={{ 
                      height: 32,
                      bgcolor: alpha(theme.palette.secondary.main, 0.12),
                      color: theme.palette.secondary.dark,
                      fontWeight: 600,
                      '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.2) }
                    }}
                  />
                  {alumni.professional_license_number && (
                    <Chip
                      icon={<WorkIcon />}
                      label={`License: ${alumni.professional_license_number}`}
                      size="medium"
                      variant="filled"
                      color="primary"
                      sx={{ height: 32, fontWeight: 600 }}
                    />
                  )}
                  {alumni.nickname && (
                    <Chip
                      label={`Nickname: ${alumni.nickname}`}
                      size="medium"
                      variant="filled"
                      sx={{ 
                        height: 32,
                        bgcolor: alpha(theme.palette.grey[500], 0.12),
                        color: '#424242',
                        fontWeight: 600,
                        '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.2) }
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Tabs */}
      <Box sx={{ px: 3 }}>
        <Card sx={{ boxShadow: 2 }}>
          <Box sx={{ borderBottom: 2, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
            <Tabs
              value={tabValue}
              onChange={(_, val) => setTabValue(val)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  minHeight: 64,
                },
              }}
            >
              <Tab label={t('alumni.tabs.profilePhotos')} icon={<PhotoLibraryIcon />} iconPosition="start" />
              <Tab label={t('alumni.tabs.personalInfo')} icon={<PersonIcon />} iconPosition="start" />
              <Tab label={t('alumni.tabs.education')} icon={<SchoolIcon />} iconPosition="start" />
              <Tab label="Work History" icon={<WorkIcon />} iconPosition="start" />
              <Tab label={t('alumni.contactAddress')} icon={<PhoneIcon />} iconPosition="start" />
              <Tab label="Home Address" icon={<LocationIcon />} iconPosition="start" />
              <Tab label="Work Address" icon={<LocationIcon />} iconPosition="start" />
              <Tab label="📋 Legal Docs" icon={<InfoIcon />} iconPosition="start" />
              {userData?.line_user_id && (
                <Tab label="🔗 LINE/LIFF Data" icon={<ChatIcon />} iconPosition="start" />
              )}
              <Tab label={t('common.info')} icon={<InfoIcon />} iconPosition="start" />
            </Tabs>
          </Box>

        {/* Tab 0: Profile Photos */}
        <TabPanel value={tabValue} index={0}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.03), border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                p: 1.5, 
                borderRadius: 2,
                display: 'flex'
              }}>
                <PhotoLibraryIcon color="primary" fontSize="large" />
              </Box>
              <Box>
                  <Typography variant="h6" fontWeight="600">
                    {t('alumni.tabs.profilePhotos')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('common.profilePhoto')}
                  </Typography>
              </Box>
              </Stack>
              {alumni?.profile_photos_updated_at && (
                <Typography variant="caption" color="text.secondary">
                  อัพเดทล่าสุด: {formatBangkokDateTime(alumni.profile_photos_updated_at)}
                </Typography>
              )}
            </Stack>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              {alumni.profile_photos && alumni.profile_photos.length > 0 ? (
                alumni.profile_photos.map((photo, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    elevation={2}
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 2,
                      border: photo.is_default ? `3px solid ${theme.palette.primary.main}` : 'none',
                    }}
                  >
                    <AuthenticatedAvatar
                      src={photo.url}
                      variant="square"
                      sx={{
                        width: '100%',
                        height: 300,
                        borderRadius: 0,
                      }}
                    />
                    {photo.is_default && (
                      <Chip
                        icon={<StarIcon />}
                        label="Default"
                        color="primary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                        }}
                      />
                    )}
                    <Box sx={{ p: 1, bgcolor: 'background.paper' }}>
                      <Typography variant="caption" color="text.secondary">
                        Uploaded: {formatDate(photo.uploaded_at)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))
            ) : (
                <Grid item xs={12}>
                  <Alert severity="info">{t('common.noData')}</Alert>
                </Grid>
              )}
            </Grid>
          </Paper>
        </TabPanel>

        {/* Tab 1: Personal Information */}
        <TabPanel value={tabValue} index={1}>
          <Stack spacing={3}>
            {/* Basic Information Card */}
            <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.03), border: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.1), 
                  p: 1.5, 
                  borderRadius: 2,
                  display: 'flex'
                }}>
                  <PersonIcon color="primary" fontSize="large" />
                </Box>
                <Box>
                    <Typography variant="h6" fontWeight="600">
                      {t('alumni.sections.basicInfo')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('alumni.tabs.personalInfo')}
                    </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.labels.title')} value={alumni.title} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.labels.nickname')} value={alumni.nickname || '-'} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.firstNameTh')} value={alumni.first_name_th} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.lastNameTh')} value={alumni.last_name_th} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.firstNameEn')} value={alumni.first_name_en} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.lastNameEn')} value={alumni.last_name_en} />
                </Grid>
              </Grid>
            </Paper>

            {/* Current Name Card (if changed) */}
            {(alumni.current_first_name_th || alumni.current_first_name_en) && (
              <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.warning.main, 0.05), border: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Box sx={{ 
                    bgcolor: alpha(theme.palette.warning.main, 0.1), 
                    p: 1.5, 
                    borderRadius: 2,
                    display: 'flex'
                  }}>
                    <PersonIcon color="warning" fontSize="large" />
                  </Box>
                  <Box>
                      <Typography variant="h6" fontWeight="600">
                        {t('alumni.sections.currentName')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('alumni.tabs.personalInfo')}
                      </Typography>
                  </Box>
                </Stack>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <InfoItem label={t('alumni.fields.currentFirstNameTh')} value={alumni.current_first_name_th || '-'} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem label={t('alumni.fields.currentLastNameTh')} value={alumni.current_last_name_th || '-'} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem label={t('alumni.fields.currentFirstNameEn')} value={alumni.current_first_name_en || '-'} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem label={t('alumni.fields.currentLastNameEn')} value={alumni.current_last_name_en || '-'} />
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Identification Card */}
            <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.03), border: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.info.main, 0.1), 
                  p: 1.5, 
                  borderRadius: 2,
                  display: 'flex'
                }}>
                  <BadgeIcon color="info" fontSize="large" />
                </Box>
                <Box>
                    <Typography variant="h6" fontWeight="600">
                      {t('alumni.sections.identification')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('alumni.sections.personalDetails')}
                    </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.birthDate')} value={formatDate(alumni.birth_date)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.gender')} value={t(`alumni.gender.${alumni.gender}`)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.labels.citizenId')} value={alumni.citizen_id} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.studentId')} value={alumni.student_id} />
                </Grid>
                {alumni.professional_license_number && (
                  <Grid item xs={12} md={6}>
                    <InfoItem label={t('alumni.fields.professionalLicenseNumber')} value={alumni.professional_license_number} />
                  </Grid>
                )}
                {alumni.batch && (
                  <Grid item xs={12} md={6}>
                    <InfoItem label={t('alumni.fields.batch')} value={alumni.batch} />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Stack>
        </TabPanel>

        {/* Tab 2: Education */}
        <TabPanel value={tabValue} index={2}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.success.main, 0.03), border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <SchoolIcon color="primary" fontSize="large" />
              <Box>
                  <Typography variant="h6" fontWeight="600">
                    {t('alumni.tabs.education')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('alumni.sections.educationInfo')}
                  </Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InfoItem label={t('alumni.fields.degree')} value={alumni.education.degree} />
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoItem label={t('alumni.fields.faculty')} value={alumni.education.faculty} />
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoItem label={t('alumni.fields.graduationYear')} value={alumni.education.graduation_year.toString()} />
              </Grid>
              {alumni.education.gpa && (
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.gpa')} value={alumni.education.gpa.toFixed(2)} />
                </Grid>
              )}
            </Grid>
          </Paper>
        </TabPanel>

        {/* Tab 4: Contact Information */}
        <TabPanel value={tabValue} index={4}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.03), border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Contact Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.1), 
                      p: 1.5, 
                      borderRadius: 2,
                      display: 'flex'
                    }}>
                      <EmailIcon color="primary" />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {t('alumni.fields.email')}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>{alumni.contact.email}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ 
                      bgcolor: alpha(theme.palette.success.main, 0.1), 
                      p: 1.5, 
                      borderRadius: 2,
                      display: 'flex'
                    }}>
                      <PhoneIcon color="success" />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {t('alumni.fields.phone')}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>{alumni.contact.phone}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
              {alumni.contact.line_id && (
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ 
                        bgcolor: alpha(theme.palette.success.main, 0.1), 
                        p: 1.5, 
                        borderRadius: 2,
                        display: 'flex'
                      }}>
                        <ChatIcon sx={{ color: '#00B900' }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          {t('alumni.fields.lineId')}
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>{alumni.contact.line_id}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              )}
              {alumni.contact.facebook && (
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ 
                        bgcolor: alpha(theme.palette.info.main, 0.1), 
                        p: 1.5, 
                        borderRadius: 2,
                        display: 'flex'
                      }}>
                        <FacebookIcon color="info" />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          {t('alumni.fields.facebookUrl')}
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>{alumni.contact.facebook}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Paper>
        </TabPanel>

        {/* Tab 5: Home Address */}
        <TabPanel value={tabValue} index={5}>
          {alumni?.address_updated_at && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                อัพเดทล่าสุด: {formatBangkokDateTime(alumni.address_updated_at)}
              </Typography>
            </Box>
          )}
          <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.03), border: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.1), 
                  p: 1.5, 
                  borderRadius: 2,
                  display: 'flex'
                }}>
                  <LocationIcon color="primary" fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="600">
                    {t('alumni.fields.homeAddress')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('alumni.sections.addressInfo')}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <InfoItem label={t('alumni.fields.addressLine')} value={alumni.address.address_line} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.subDistrict')} value={alumni.address.sub_district} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.district')} value={alumni.address.district} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.province')} value={alumni.address.province} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.postalCode')} value={alumni.address.postal_code} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label={t('alumni.fields.country')} value={alumni.address.country} />
                </Grid>
              </Grid>
            </Paper>
        </TabPanel>

        {/* Tab 6: Work Address */}
        <TabPanel value={tabValue} index={6}>
          {alumni?.work_address_updated_at && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                อัพเดทล่าสุด: {formatBangkokDateTime(alumni.work_address_updated_at)}
              </Typography>
            </Box>
          )}
          {alumni.work_address ? (
            <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.warning.main, 0.03), border: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Box sx={{ 
                    bgcolor: alpha(theme.palette.warning.main, 0.1), 
                    p: 1.5, 
                    borderRadius: 2,
                    display: 'flex'
                  }}>
                    <WorkIcon color="warning" fontSize="large" />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="600">
                      {t('alumni.fields.workAddress')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('alumni.sections.workAddressInfo')}
                    </Typography>
                  </Box>
                </Stack>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <InfoItem label={t('alumni.fields.addressLine')} value={alumni.work_address.address_line} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem label={t('alumni.fields.subDistrict')} value={alumni.work_address.sub_district} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem label={t('alumni.fields.district')} value={alumni.work_address.district} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem label={t('alumni.fields.province')} value={alumni.work_address.province} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem label={t('alumni.fields.postalCode')} value={alumni.work_address.postal_code} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem label={t('alumni.fields.country')} value={alumni.work_address.country} />
                  </Grid>
                </Grid>
              </Paper>
          ) : (
            <Alert severity="info">ยังไม่มีข้อมูลที่อยู่ที่ทำงาน</Alert>
          )}
        </TabPanel>

        {/* Tab 3: Work History */}
        <TabPanel value={tabValue} index={3}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.warning.main, 0.03), border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ 
                bgcolor: alpha(theme.palette.warning.main, 0.1), 
                p: 1.5, 
                borderRadius: 2,
                display: 'flex'
              }}>
                <WorkIcon color="warning" fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Work History
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Employment history and experience
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 3 }} />
            {alumni.work_history && alumni.work_history.length > 0 ? (
              <Stack spacing={2}>
                {alumni.work_history.map((work, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 3,
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      borderLeft: 4,
                      borderLeftColor: work.is_current ? 'success.main' : 'grey.300',
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="h6" fontWeight="600" color="primary">
                            {work.position}
                          </Typography>
                          {work.is_current && (
                            <Chip
                              label="Current"
                              size="small"
                              color="success"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Stack>
                        <Typography variant="body1" fontWeight="500" color="text.secondary" sx={{ mt: 0.5 }}>
                          {work.company}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <InfoItem
                          label="Start Date"
                          value={new Date(work.start_date).toLocaleDateString('th-TH', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        />
                      </Grid>
                      {work.end_date && (
                        <Grid item xs={12} md={6}>
                          <InfoItem
                            label="End Date"
                            value={new Date(work.end_date).toLocaleDateString('th-TH', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          />
                        </Grid>
                      )}
                      {work.description && (
                        <Grid item xs={12}>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              gutterBottom
                              display="block"
                              fontWeight={600}
                              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                            >
                              Description
                            </Typography>
                            <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                              {work.description}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {work.updated_at && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Last updated: {formatBangkokDateTime(work.updated_at)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Alert severity="info">No work history available</Alert>
            )}
          </Paper>
        </TabPanel>

        {/* Tab 6: Additional Information */}
        <TabPanel value={tabValue} index={6}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.success.main, 0.03), border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ 
                bgcolor: alpha(theme.palette.success.main, 0.1), 
                p: 1.5, 
                borderRadius: 2,
                display: 'flex'
              }}>
                <InfoIcon color="success" fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Additional Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Occupation, ideals, and other information
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              {alumni.current_occupation && (
                <Grid item xs={12}>
                  <InfoItem label={t('alumni.fields.currentOccupation')} value={alumni.current_occupation} />
                </Grid>
              )}
              {alumni.ideals && alumni.ideals.length > 0 && (
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                      {t('alumni.fields.ideals')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {alumni.ideals.map((ideal, index) => (
                        <Chip key={index} label={ideal} size="medium" variant="outlined" color="primary" />
                      ))}
                    </Stack>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </TabPanel>

        {/* Tab 7: Legal Documents */}
        <TabPanel value={tabValue} index={7}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.03), border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ 
                bgcolor: alpha(theme.palette.info.main, 0.1), 
                p: 1.5, 
                borderRadius: 2,
                display: 'flex'
              }}>
                <InfoIcon color="info" fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  📋 Legal Documents
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  User agreements, privacy policy, and consent records
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 3 }} />
            
            <Stack spacing={2}>
              <Alert severity="info">
                View legal documents and user consents that this alumni has agreed to.
              </Alert>
              
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<InfoIcon />}
                  onClick={() => window.open('/legal/terms', '_blank')}
                  sx={{ mr: 1, mb: 1 }}
                >
                  📜 Terms of Service
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<InfoIcon />}
                  onClick={() => window.open('/legal/privacy', '_blank')}
                  sx={{ mr: 1, mb: 1 }}
                >
                  🔒 Privacy Policy
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<InfoIcon />}
                  onClick={() => window.open('/legal/consent', '_blank')}
                  sx={{ mr: 1, mb: 1 }}
                >
                  ✅ Data Consent
                </Button>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                To manage full consent records and view user's consent history, please visit the{' '}
                <Button
                  size="small"
                  onClick={() => window.location.href = '/legal-documents'}
                  sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                >
                  Legal Documents Management
                </Button>{' '}
                page.
              </Typography>
            </Stack>
          </Paper>
        </TabPanel>

        {/* Tab 8: LINE/LIFF Data */}
        {userData?.line_user_id && (
          <TabPanel value={tabValue} index={8}>
            {userData ? (
            <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.03), border: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.info.main, 0.1), 
                  p: 1.5, 
                  borderRadius: 2,
                  display: 'flex'
                }}>
                  <ChatIcon color="info" fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={600} color="info.main">
                    🔗 LINE & LIFF Integration Data
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Complete LINE profile and LIFF SDK information captured during registration
                  </Typography>
                </Box>
              </Stack>

              {/* LINE Profile Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                  📱 LINE Profile
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <InfoItem label="LINE User ID" value={userData.line_user_id || 'N/A'} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem label="LINE Display Name" value={userData.line_display_name || 'N/A'} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem label="Email" value={userData.email || 'N/A'} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem label="User Role" value={userData.role || 'N/A'} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Profile Pictures
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        {userData.line_picture_url && (
                          <Box>
                            <Typography variant="caption" display="block" gutterBottom>LINE Picture</Typography>
                            <img src={userData.line_picture_url} alt="LINE Profile" style={{ width: 64, height: 64, borderRadius: 8 }} />
                          </Box>
                        )}
                        {userData.avatar_url && (
                          <Box>
                            <Typography variant="caption" display="block" gutterBottom>Avatar (Downloaded)</Typography>
                            <img src={userData.avatar_url} alt="Avatar" style={{ width: 64, height: 64, borderRadius: 8 }} />
                          </Box>
                        )}
                        {!userData.line_picture_url && !userData.avatar_url && (
                          <Typography variant="body2" color="text.secondary">No pictures available</Typography>
                        )}
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* LIFF Context Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                  🌐 LIFF Context
                </Typography>
                {userData.liff_context ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <InfoItem label="Context Type" value={userData.liff_context.type || 'N/A'} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoItem label="View Type" value={userData.liff_context.viewType || 'N/A'} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoItem label="User ID" value={userData.liff_context.userId || 'N/A'} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoItem label="Utou ID" value={userData.liff_context.utouId || 'N/A'} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoItem label="Room ID" value={userData.liff_context.roomId || 'N/A'} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoItem label="Group ID" value={userData.liff_context.groupId || 'N/A'} />
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No LIFF context data available
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Device Info Section */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                  📱 Device & Environment
                </Typography>
                {userData.device_info ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <InfoItem label="Operating System" value={userData.device_info.os || 'N/A'} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoItem label="Language" value={userData.device_info.language || 'N/A'} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoItem label="LIFF Version" value={userData.device_info.liff_version || 'N/A'} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoItem label="LINE Version" value={userData.device_info.line_version || 'N/A'} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoItem 
                        label="In LINE Client" 
                        value={userData.device_info.is_in_client !== undefined ? 
                          (userData.device_info.is_in_client ? '✅ Yes' : '❌ No') : 'N/A'
                        } 
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoItem 
                        label="Logged In" 
                        value={userData.device_info.is_logged_in !== undefined ? 
                          (userData.device_info.is_logged_in ? '✅ Yes' : '❌ No') : 'N/A'
                        } 
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No device information available
                  </Typography>
                )}
              </Box>
            </Paper>
          ) : (
            <Alert severity="info">
              <AlertTitle>No LINE Integration</AlertTitle>
              This alumni has not registered or logged in via LINE/LIFF. No LINE profile or LIFF SDK data available.
            </Alert>
          )}
          </TabPanel>
        )}

        {/* Tab 9: Info (System Information) */}
        <TabPanel value={tabValue} index={userData?.line_user_id ? 9 : 8}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.warning.main, 0.03), border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ 
                bgcolor: alpha(theme.palette.warning.main, 0.1), 
                p: 1.5, 
                borderRadius: 2,
                display: 'flex'
              }}>
                <InfoIcon color="warning" fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  System Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Metadata and system details
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InfoItem label={t('alumni.fields.alumniType')} value={t(`alumni.type.${alumni.alumni_type}`)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoItem label={t('alumni.approvalStatus')} value={t(`common.${alumni.approval_status}`)} />
              </Grid>
            </Grid>
          </Paper>
        </TabPanel>
      </Card>
      </Box>
    </Box>
  );
}

// Helper component for displaying information
interface InfoItemProps {
  label: string;
  value: string;
}

function InfoItem({ label, value }: InfoItemProps) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" gutterBottom display="block" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={600} color="text.primary">
        {value}
      </Typography>
    </Box>
  );
}

export default AlumniDetail;
